/**
 * Analytics & Tracking Service
 * Integrates with Google Analytics 4, Facebook Pixel, TikTok Pixel, and custom tracking
 */

declare global {
  interface Window {
    gtag: any;
    fbq: any;
    _fbq: any;
    ttq: any;
    dataLayer: any[];
    lintrk: any;
    pintrk: any;
    hj: any;
    _hjSettings: any;
  }
}

export type AnalyticsEvent =
  | 'page_view'
  | 'view_item'
  | 'view_item_list'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'begin_checkout'
  | 'purchase'
  | 'search'
  | 'sign_up'
  | 'login'
  | 'share'
  | 'skin_analysis_complete'
  | 'quiz_complete'
  | 'add_to_wishlist'
  | 'contact'
  | 'generate_lead'
  | 'complete_registration'
  | 'find_location'
  | 'schedule';

export interface ProductData {
  id: string;
  name: string;
  category?: string;
  brand?: string;
  variant?: string;
  price: number;
  quantity?: number;
  currency?: string;
}

export interface UserProperties {
  skin_type?: string;
  age_group?: string;
  gender?: string;
  customer_ltv?: number;
  membership_tier?: string;
  preferred_category?: string;
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const FB_ID = import.meta.env.VITE_FB_PIXEL_ID;
const TIKTOK_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID;

function isRealId(value: string | undefined): value is string {
  if (!value) return false;
  const v = value.trim();
  if (v.length < 4) return false;
  const placeholders = ['GA_MEASUREMENT_ID', 'FB_PIXEL_ID', 'TIKTOK_PIXEL_ID', 'HOTJAR_ID',
    'GTM-XXXXXXX', 'GTM_XXXXXXX', 'CLARITY_PROJECT_ID', 'PINTEREST_TAG_ID',
    'LINKEDIN_PARTNER_ID', 'KLAVIYO_ID', 'ZENDESK_KEY', 'YOUR_', 'REPLACE_',
    'XXXXXXXX', 'XXXXXXX'];
  return !placeholders.some(p => v.toUpperCase().includes(p));
}

class AnalyticsService {
  private isInitialized = false;
  private userId: string | null = null;
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Initialize all tracking pixels
  initialize() {
    if (this.isInitialized) return;

    // gtag/fbq/ttq calls happen unconditionally throughout this service —
    // stub them so tracking is a harmless no-op when a real ID isn't
    // configured (e.g. this environment), instead of throwing and taking
    // down whatever page triggered it.
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.fbq = window.fbq || function () {};
    window.ttq = window.ttq || [];

    if (isRealId(GA_ID)) this.initGoogleAnalytics();
    if (isRealId(FB_ID)) this.initFacebookPixel();
    if (isRealId(TIKTOK_ID)) this.initTikTokPixel();
    this.initHotjar(); // no-op — loaded via script tag

    this.isInitialized = true;
  }

  // Google Analytics 4
  private initGoogleAnalytics() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID!, {
      send_page_view: false,
      cookie_flags: 'SameSite=None;Secure',
      custom_map: {
        custom_parameter_1: 'skin_type',
        custom_parameter_2: 'skin_concern',
      },
    });
  }

  // Facebook Pixel
  private initFacebookPixel() {
    window.fbq = window.fbq || function() {
      window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = window.fbq;
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = [];

    window.fbq('init', FB_ID!);
    window.fbq('track', 'PageView');
  }

  // TikTok Pixel
  private initTikTokPixel() {
    window.ttq = window.ttq || [];
    window.ttq.push(['init', TIKTOK_ID!]);
  }

  // Hotjar Heatmaps
  private initHotjar() {
    // Hotjar will be loaded via script
  }

  // Track page view
  pageView(path: string, title?: string) {
    if (!this.isInitialized) this.initialize();

    const pageData = {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
      send_to: isRealId(GA_ID) ? GA_ID : undefined,
    };

    // Google Analytics
    window.gtag('event', 'page_view', pageData);

    // Facebook Pixel
    window.fbq('track', 'PageView', {
      pagePath: path,
      pageTitle: title,
    });

    // TikTok
    if (typeof window.ttq?.track === 'function') {
      window.ttq.track('Browse', { url: window.location.href });
    }

    this.logToConsole('page_view', pageData);
  }

  // Track product view
  viewItem(product: ProductData) {
    const eventData = {
      currency: product.currency || 'ZAR',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        item_brand: product.brand || 'Marple',
        price: product.price,
        quantity: product.quantity || 1,
      }],
    };

    // GA4
    window.gtag('event', 'view_item', eventData);

    // Facebook
    window.fbq('track', 'ViewContent', {
      content_ids: [product.id],
      content_type: 'product',
      content_name: product.name,
      content_category: product.category,
      value: product.price,
      currency: product.currency || 'ZAR',
    });

    // TikTok
    if (typeof window.ttq?.track === 'function') {
      window.ttq.track('ViewContent', {
        content_id: product.id,
        content_type: 'product',
        content_name: product.name,
        price: product.price,
        currency: product.currency || 'ZAR',
      });
    }

    this.logToConsole('view_item', eventData);
  }

  // Track product list view
  viewItemList(items: ProductData[], listName: string) {
    const eventData = {
      item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
      item_list_name: listName,
      items: items.map((product, index) => ({
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        item_list_position: index + 1,
        price: product.price,
      })),
    };

    window.gtag('event', 'view_item_list', eventData);
    this.logToConsole('view_item_list', eventData);
  }

  // Track add to cart
  addToCart(product: ProductData, quantity: number = 1) {
    const eventData = {
      currency: product.currency || 'ZAR',
      value: product.price * quantity,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: quantity,
      }],
    };

    // GA4
    window.gtag('event', 'add_to_cart', eventData);

    // Facebook
    window.fbq('track', 'AddToCart', {
      content_ids: [product.id],
      content_type: 'product',
      content_name: product.name,
      value: product.price * quantity,
      currency: product.currency || 'ZAR',
    });

    // TikTok
    if (typeof window.ttq?.track === 'function') {
      window.ttq.track('AddToCart', {
        content_id: product.id,
        content_type: 'product',
        content_name: product.name,
        quantity: quantity,
        price: product.price,
        currency: product.currency || 'ZAR',
      });
    }

    this.logToConsole('add_to_cart', eventData);
  }

  // Track remove from cart
  removeFromCart(product: ProductData, quantity: number = 1) {
    const eventData = {
      currency: product.currency || 'ZAR',
      value: product.price * quantity,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: quantity,
      }],
    };

    window.gtag('event', 'remove_from_cart', eventData);
    this.logToConsole('remove_from_cart', eventData);
  }

  // Track begin checkout
  beginCheckout(items: ProductData[], value: number) {
    const eventData = {
      currency: 'ZAR',
      value: value,
      items: items.map(product => ({
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: product.quantity || 1,
      })),
    };

    // GA4
    window.gtag('event', 'begin_checkout', eventData);

    // Facebook
    window.fbq('track', 'InitiateCheckout', {
      content_ids: items.map(i => i.id),
      content_type: 'product',
      value: value,
      currency: 'ZAR',
      num_items: items.length,
    });

    // TikTok
    if (typeof window.ttq?.track === 'function') {
      window.ttq.track('StartCheckout', {
        content_ids: items.map(i => i.id),
        value: value,
        currency: 'ZAR',
      });
    }

    this.logToConsole('begin_checkout', eventData);
  }

  // Track purchase
  purchase(
    transactionId: string,
    items: ProductData[],
    value: number,
    shipping: number = 0,
    tax: number = 0
  ) {
    const eventData = {
      transaction_id: transactionId,
      value: value,
      currency: 'ZAR',
      tax: tax,
      shipping: shipping,
      items: items.map(product => ({
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: product.quantity || 1,
      })),
    };

    // GA4
    window.gtag('event', 'purchase', eventData);

    // Facebook
    window.fbq('track', 'Purchase', {
      content_ids: items.map(i => i.id),
      content_type: 'product',
      value: value,
      currency: 'ZAR',
      num_items: items.reduce((sum, i) => sum + (i.quantity || 1), 0),
    });

    // TikTok
    if (typeof window.ttq?.track === 'function') {
      window.ttq.track('CompletePayment', {
        content_ids: items.map(i => i.id),
        value: value,
        currency: 'ZAR',
        transaction_id: transactionId,
      });
    }

    this.logToConsole('purchase', eventData);
  }

  // Track skin analysis completion
  skinAnalysisComplete(result: {
    skinType: string;
    concerns: string[];
    recommendedProducts: string[];
  }) {
    const eventData = {
      skin_type: result.skinType,
      skin_concerns: result.concerns,
      num_recommendations: result.recommendedProducts.length,
    };

    window.gtag('event', 'skin_analysis_complete', eventData);
    
    window.fbq('track', 'Lead', {
      content_name: 'Skin Analysis',
      skin_type: result.skinType,
    });

    this.logToConsole('skin_analysis_complete', eventData);
  }

  // Track quiz completion
  quizComplete(result: {
    skinType: string;
    discountCode: string;
  }) {
    const eventData = {
      skin_type: result.skinType,
      discount_code: result.discountCode,
    };

    window.gtag('event', 'quiz_complete', eventData);
    
    window.fbq('track', 'CompleteRegistration', {
      content_name: 'Skin Quiz',
      skin_type: result.skinType,
    });

    this.logToConsole('quiz_complete', eventData);
  }

  // Track search
  search(query: string, resultsCount: number) {
    const eventData = {
      search_term: query,
      results_count: resultsCount,
    };

    window.gtag('event', 'search', eventData);
    this.logToConsole('search', eventData);
  }

  // Track add to wishlist
  addToWishlist(product: ProductData) {
    const eventData = {
      currency: product.currency || 'ZAR',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
      }],
    };

    window.gtag('event', 'add_to_wishlist', eventData);
    this.logToConsole('add_to_wishlist', eventData);
  }

  // Track sign up
  signUp(method: string) {
    const eventData = { method };

    window.gtag('event', 'sign_up', eventData);
    window.fbq('track', 'CompleteRegistration', { method });
    this.logToConsole('sign_up', eventData);
  }

  // Track login
  login(method: string) {
    const eventData = { method };

    window.gtag('event', 'login', eventData);
    this.logToConsole('login', eventData);
  }

  // Track share
  share(method: string, contentType: string, itemId: string) {
    const eventData = {
      method,
      content_type: contentType,
      item_id: itemId,
    };

    window.gtag('event', 'share', eventData);
    this.logToConsole('share', eventData);
  }

  // Set user properties
  setUserProperties(properties: UserProperties) {
    window.gtag('set', 'user_properties', properties);
    this.logToConsole('set_user_properties', properties);
  }

  // Set user ID (for logged in users)
  setUserId(userId: string) {
    this.userId = userId;
    if (isRealId(GA_ID)) {
      window.gtag('config', GA_ID, {
        user_id: userId,
      });
    }
  }

  // Track custom event
  trackEvent(eventName: string, params?: Record<string, any>) {
    window.gtag('event', eventName, params);
    this.logToConsole(eventName, params);
  }

  // Track affiliate click
  affiliateClick(agentId: string, productId: string) {
    const eventData = {
      agent_id: agentId,
      product_id: productId,
    };

    window.gtag('event', 'affiliate_click', eventData);
    this.logToConsole('affiliate_click', eventData);
  }

  // Track agent registration
  agentRegistration(agentData: {
    agentId: string;
    referrerId?: string;
  }) {
    const eventData = {
      agent_id: agentData.agentId,
      referrer_id: agentData.referrerId,
    };

    window.gtag('event', 'agent_registration', eventData);
    window.fbq('track', 'CompleteRegistration', {
      content_name: 'Agent Registration',
    });
    this.logToConsole('agent_registration', eventData);
  }

  // Track commission earned
  commissionEarned(agentId: string, amount: number, tier: number) {
    const eventData = {
      agent_id: agentId,
      value: amount,
      currency: 'ZAR',
      tier: tier,
    };

    window.gtag('event', 'commission_earned', eventData);
    this.logToConsole('commission_earned', eventData);
  }

  // Get attribution data from URL
  getAttributionData(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
      fbclid: params.get('fbclid') || '',
      gclid: params.get('gclid') || '',
      ttclid: params.get('ttclid') || '',
      ref: params.get('ref') || '',
      agent: params.get('agent') || '',
    };
  }

  // Send attribution data
  sendAttribution() {
    const attribution = this.getAttributionData();
    
    // Store in session
    sessionStorage.setItem('marple_attribution', JSON.stringify(attribution));
    
    // Send to analytics
    window.gtag('event', 'attribution_data', attribution);
    
    this.logToConsole('attribution_data', attribution);
  }

  // Console logging for development
  private logToConsole(eventName: string, data: any) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] ${eventName}:`, data);
    }
  }

  // Get analytics dashboard URLs
  getDashboardUrls() {
    return {
      ga4: 'https://analytics.google.com/analytics/web/',
      facebook: 'https://business.facebook.com/events_manager',
      tiktok: 'https://ads.tiktok.com/i18n/home',
      hotjar: 'https://insights.hotjar.com/',
      searchConsole: 'https://search.google.com/search-console',
    };
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
